<template>
  <Page
    :loading="isLoadingPage"
    :spinning="isSpinning"
    class="base-table"
  >
    <PageTitle>Ændringer i medicin</PageTitle>

    <Box
      class="jumpToMenu"
      v-if="showJumpMenu"
    >
      <p
        class="jumpToMenu__header"
        ref="menu"
      >
        Hop til:
      </p>
      <div class="jumpToMenu__section">
        <p>Indholdsstoffer:</p>
        <ul>
          <li @click="scrollTo('newSubstances')">
            Nye
          </li>
          <li @click="scrollTo('reopenedSubstances')">
            Genoptagede
          </li>
          <li @click="scrollTo('deletedSubstances')">
            Udgået
          </li>
          <li @click="scrollTo('updatedSubstancesWithMissingStatuses')">
            Ændrede
          </li>
        </ul>
      </div>
      <div class="jumpToMenu__section">
        <p>Præparater:</p>
        <ul>
          <li @click="scrollTo('newPreparations')">
            Nye
          </li>
          <li @click="scrollTo('deletedPreparations')">
            Udgået
          </li>
          <li @click="scrollTo('updatedPreparations')">
            Ændrede
          </li>
        </ul>
      </div>
    </Box>

    <Box
      class="jumpToMenu"
      v-if="showOverrule"
    >
      <BaseCheckbox
        v-model="overruleNotAllowed"
        label="Aktivér manuel styring af oprettelsesflow"
      />
    </Box>

    <!-- Route of administration -->
    <div class="row importSection">
      <div class="col-xs-6">
        <h3>Administrationsveje, som ikke er grupperet</h3>
        <Box>
          <ImportRouteOfAdministrationList
            v-if="unusedImportCodes.length"
            :list="unusedImportCodes"
          />
          <p v-else>
            Ingen data.
          </p>
        </Box>
      </div>
    </div>

    <!-- Substances -->
    <div class="row importSection">
      <div class="col-xs-6">
        <div class="row">
          <h3
            class="col-xs-12"
            ref="newSubstances"
          >
            Indholdsstoffer - Nye
            <span
              class="jumpToMenu-gotoTop"
              @click="scrollTo('menu')"
              v-if="showJumpMenu"
            >(Gå til top)</span>
          </h3>
          <div class="col-xs-12">
            <Box :class="{'not-allowed': !isSubstancesAllowed}">
              <ImportSubstanceList
                v-if="newSubstances"
                :list="newSubstances"
                @click="onCreateNewSubstance"
                label="Opret"
              />
              <p v-else>
                Ingen data.
              </p>
            </Box>
          </div>
        </div>
        <div class="row">
          <h3
            class="col-xs-12"
            ref="reopenedSubstances"
          >
            Indholdsstoffer - Genoptagede
            <span
              class="jumpToMenu-gotoTop"
              @click="scrollTo('menu')"
              v-if="showJumpMenu"
            >(Gå til top)</span>
          </h3>
          <div class="col-xs-12">
            <Box :class="{'not-allowed': !isSubstancesAllowed}">
              <ImportSubstanceList
                v-if="reopenedSubstances.length"
                :list="reopenedSubstances"
                @click="onReopenedSubstance"
                label="Opret version"
              />
              <p v-else>
                Ingen data.
              </p>
            </Box>
          </div>
        </div>
        <div class="row">
          <h3
            class="col-xs-12"
            ref="deletedSubstances"
          >
            Indholdsstoffer - Udgået
            <span
              class="jumpToMenu-gotoTop"
              @click="scrollTo('menu')"
              v-if="showJumpMenu"
            >(Gå til top)</span>
          </h3>
          <div class="col-xs-12">
            <Box :class="{'not-allowed': !isSubstancesAllowed}">
              <ImportSubstanceList
                v-if="deletedSubstances.length"
                :list="deletedSubstances"
                @click="onDeletedSubstance"
                label="Opret version"
              />
              <p v-else>
                Ingen data.
              </p>
            </Box>
          </div>
        </div>
      </div>
    </div>

    <!-- Preparations -->
    <div class="row importSection">
      <div class="col-xs-12">
        <h3 ref="newPreparations">
          Præparater - Nye
          <span
            class="jumpToMenu-gotoTop"
            @click="scrollTo('menu')"
            v-if="showJumpMenu"
          >(Gå til top)</span>
        </h3>
        <div>
          <Box :class="{'not-allowed': !isPreparationsAllowed}">
            <ImportPreparationNewList
              v-if="newPreparations.length"
              :list="newPreparations"
              @click="onCreateNewPreparation"
            />
            <p v-else>
              Ingen data
            </p>
          </Box>
        </div>
        <div class="row">
          <div class="col-xs-6">
            <h3 ref="deletedPreparations">
              Præparater - Udgået
              <span
                class="jumpToMenu-gotoTop"
                @click="scrollTo('menu')"
                v-if="showJumpMenu"
              >(Gå til top)</span>
            </h3>
            <div>
              <Box :class="{'not-allowed': !isPreparationsAllowed}">
                <ImportPreparationDeletedList
                  v-if="deletedPreparations.length"
                  :list="deletedPreparations"
                  @click="onDeletedPreparation"
                />
                <p v-else>
                  Ingen data
                </p>
              </Box>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-12">
            <div class="updateProductNumbers">
              <h3 ref="updatedPreparations">
                Præparater - Ændrede
                <span
                  class="jumpToMenu-gotoTop"
                  @click="scrollTo('menu')"
                  v-if="showJumpMenu"
                >(Gå til top)</span>
              </h3>
              <BaseButton
                @click="updateAllProductNumbers"
                label="Opdatér alle præparater med ændringer i varenumre"
              />
            </div>
            <div>
              <Box :class="{'not-allowed': !isPreparationsAllowed}">
                <ImportPreparationUpdatedList
                  v-if="updatedPreparations.length"
                  :list="updatedPreparations"
                  @click="onUpdatePreparation"
                />
                <p v-else>
                  Ingen data
                </p>
              </Box>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Updated substances with missing statuses -->
    <div class="row importSection">
      <div class="col-xs-12">
        <h3 ref="updatedSubstancesWithMissingStatuses">
          Indholdsstoffer - Ændrede
          <span
            class="jumpToMenu-gotoTop"
            @click="scrollTo('menu')"
            v-if="showJumpMenu"
          >(Gå til top)</span>
        </h3>
        <div>
          <Box>
            <ImportSubstanceUpdatedList
              v-if="updatedSubstancesWithMissingStatuses.length"
              :list="updatedSubstancesWithMissingStatuses"
            />
            <p v-else>
              Ingen data
            </p>
          </Box>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import BaseButton from '@/components/BaseButton.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import ImportRouteOfAdministrationList from './ImportRouteOfAdministrationList.vue';
import ImportPreparationDeletedList from './ImportPreparationDeletedList.vue';
import ImportPreparationUpdatedList from './ImportPreparationUpdatedList.vue';
import ImportPreparationNewList from './ImportPreparationNewList.vue';
import ImportSubstanceList from './ImportSubstanceList.vue';
import Box from '@/components/Box.vue';
import Page from '../Page.vue';
import PageTitle from '../../components/PageTitle.vue';
import Pageload from '../../mixins/Pageload';
import ImportdataService from '../../services/ImportdataService';
import RouteOfAdministrationService from '../../services/RouteOfAdministrationService';
import HumanDate from '../../filters/HumanDate';
import ImportSubstanceUpdatedList from './ImportSubstanceUpdatedList.vue';

export default {
    name: 'ImportData',
    mixins: [Pageload],
    components: {
        PageTitle,
        Page,
        Box,
        ImportSubstanceList,
        ImportPreparationNewList,
        ImportPreparationUpdatedList,
        ImportPreparationDeletedList,
        ImportRouteOfAdministrationList,
        BaseCheckbox,
        BaseButton,
        ImportSubstanceUpdatedList,
    },
    filters: { HumanDate },
    data() {
        return {
            unusedImportCodes: [],
            newSubstances: [],
            deletedSubstances: [],
            reopenedSubstances: [],
            newPreparations: [],
            updatedPreparations: [],
            deletedPreparations: [],
            overruleNotAllowed: false,
            updatedSubstancesWithMissingStatuses: [],
        };
    },
    computed: {
        showOverrule() {
            return (this.preparationsLength > 0 && this.substanesLength > 0) || (this.substanesLength > 0 && this.unusedImportCodesLength > 0);
        },
        substanesLength() {
            return this.newSubstances.length + this.deletedSubstances.length + this.reopenedSubstances.length;
        },
        preparationsLength() {
            return this.updatedPreparations.length + this.deletedPreparations.length + this.newPreparations.length;
        },
        unusedImportCodesLength() {
            return this.unusedImportCodes.length;
        },
        isSubstancesAllowed() {
            return this.unusedImportCodesLength === 0 || this.overruleNotAllowed;
        },
        isPreparationsAllowed() {
            return this.substanesLength === 0 || this.overruleNotAllowed;
        },
        showJumpMenu() {
            let totalLength = 0;

            totalLength += this.unusedImportCodes.length;
            totalLength += this.newSubstances.length;
            totalLength += this.deletedSubstances.length;
            totalLength += this.reopenedSubstances.length;
            totalLength += this.newPreparations.length;
            totalLength += this.updatedPreparations.length;
            totalLength += this.deletedPreparations.length;

            return totalLength > 200;
        },
    },
    methods: {
        scrollTo(refName) {
            const element = this.$refs[refName];
            element.scrollIntoView();
        },
        onCreateNewSubstance(item) {
            this.$router.push({
                name: 'substances.create',
                params: {
                    defaultForm: {
                        name: item.name,
                    },
                },
            });
        },
        onReopenedSubstance(item) {
            this.$router.push({
                name: 'substances.single',
                params: {
                    fromImport: true,
                    id: item.id,
                    defaultMode: 'newVersion',
                    defaultItem: {
                        deleted: false,
                    },
                },
            });
        },
        onDeletedSubstance(item) {
            this.$router.push({
                name: 'substances.single',
                params: {
                    fromImport: true,
                    id: item.id,
                    defaultMode: 'newVersion',
                    defaultItem: {
                        deleted: true,
                    },
                },
            });
        },
        onCreateNewPreparation(item) {
            this.$router.push({
                name: 'preparations.create',
                params: {
                    defaultItem: item,
                },
            });
        },
        onUpdatePreparation(item) {
            this.$router.push({
                name: 'preparations.single',
                params: {
                    fromImport: true,
                    id: item.id,
                    defaultMode: 'newVersion',
                    updatedRelations: {
                        substances: item.substances,
                        routeOfAdministrations: item.routeOfAdministrations,
                        productNumbers: item.productNumbers,
                    },
                },
            });
        },
        onDeletedPreparation(item) {
            this.$router.push({
                name: 'preparations.single',
                params: {
                    fromImport: true,
                    id: item.id,
                    defaultMode: 'newVersion',
                    defaultItem: {
                        deleted: true,
                        publishDate: item.changeDate || window.moment(),
                    },
                },
            });
        },
        async updateAllProductNumbers() {
            // eslint-disable-next-line no-alert
            if (window.confirm('Ønsker du at fortsætte?\nOpdatér alle præparater med ændringer i varenumre')) {
                try {
                    await ImportdataService.autoUpdateProductNumbers();
                    this.$snotify.success('Alle præparater med ændringer i varenumre er opdateret');

                    this.reloadPage();
                    this.loadAllData();
                    return;
                } catch (error) {
                    this.$snotify.error('Der opstod en fejl.');
                    return;
                }
            }

            this.$snotify.info('Handlingen blev annulleret.');
        },
        async loadAllData() {
            try {
                this.unusedImportCodes = await RouteOfAdministrationService.unusedImportCodes();
                const overview = await ImportdataService.overview();
                this.newSubstances = overview.newSubstances;
                this.reopenedSubstances = overview.reopenedSubstances;
                this.deletedSubstances = overview.deletedSubstances;
                this.newPreparations = overview.newPreparations;
                this.updatedPreparations = overview.updatedPreparations;
                this.deletedPreparations = overview.deletedPreparations;
                this.updatedSubstancesWithMissingStatuses = overview.updatedSubstancesWithMissingStatuses;
                // this.loadNotificationList();
                this.pageloaded();
            } catch (error) {
            //
            }
        },
    },
    async created() {
        this.loadAllData();
    },
};
</script>

<style lang="scss" scoped>
.jumpToMenu {
    background: #fefefe;
    padding: 10px;
    max-width: 400px;
    margin-bottom: 50px;

    &-gotoTop {
        font-size: 12px;
        cursor: pointer;
        opacity: 0.5;
    }

    &__header {
        margin-right: 10px;
        font-weight: bold;
        margin-bottom: 10px;
    }

    &__section {
        display: flex;
        &:not(:last-child) {
            margin-bottom: 10px;
        }
        p {
            font-weight: bold;
            width: 120px;
        }
    }
    ul {
        list-style: none;
        display: flex;

        li {
            text-decoration: underline;
            cursor: pointer;

            &:not(:last-child) {
                margin-right: 10px;
            }
        }
    }
}

.importSection {
    &:not(:last-child) {
        margin-bottom: 60px;
    }
}
.routeOfStatus {
    display: flex;
    justify-content: space-between;

    &__link {
        font-weight: normal;
        text-align: right;
        user-select: none;
        display: inline-block;

        &:hover {
            text-decoration: underline;
        }
    }
}

.not-allowed {
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
}

.updateProductNumbers {
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        margin: 0;
    }
}
</style>
